import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  requestPasswordReset,
  resetPassword,
  logoutMmt,
} from "../mmtUserList/mmtApi";
import { useSection } from "../../i18n/useSection";
import { useT } from "../../i18n/useT";
import { clearStoredSession, getStoredSession } from "../mmtUserList/mmtFlow";
import AuthLayout from "./AuthLayout";
import PasswordRule from "./PasswordRule";
import "./mmtlogin.scss";

const STEPS = { EMAIL: "email", RESET: "reset", SUCCESS: "success" };
const EMAIL_REGEX = /^\s*[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,}\s*$/i;

export default function MmtForgotPasswordFlow({
  changePassword = false,
  accountEmail = "",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useSection("mmtForgot");
  const passwordLabels = useSection("mmtLogin");
  const p = useT("population");
  const initialEmail = accountEmail || location.state?.email || "";
  const autoSend = Boolean(location.state?.autoSend && initialEmail);
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const redirectTimer = useRef(null);
  useEffect(() => () => window.clearTimeout(redirectTimer.current), []);

  const run = async (fn) => {
    setLoading(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t.error_generic);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoSend) return;

    run(async () => {
      await requestPasswordReset(initialEmail);
      setStep(STEPS.RESET);
    });
  }, [autoSend, initialEmail]);

  const submitEmail = (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError(t.error_invalid_email);
      return;
    }
    run(async () => {
      await requestPasswordReset(trimmed);
      setStep(STEPS.RESET);
    });
  };

  const submitReset = (event) => {
    event.preventDefault();
    if (!otp.trim()) {
      setError(t.error_missing_code);
      return;
    }
    if (password.length < 8) {
      setError(t.error_password_length);
      return;
    }
    if (password !== confirm) {
      setError(t.error_password_mismatch);
      return;
    }
    run(async () => {
      await resetPassword({ email: email.trim(), otp: otp.trim(), password });
      if (changePassword) {
        const session = getStoredSession();
        if (session) await logoutMmt(session);
        clearStoredSession();
      }
      setStep(STEPS.SUCCESS);
      redirectTimer.current = window.setTimeout(
        () => navigate("/login", { replace: true }),
        1200,
      );
    });
  };

  return (
    <AuthLayout
      screen={`${step}:${t.title}`}
      back={
        changePassword &&
        step !== STEPS.SUCCESS && (
          <Link className="mmt-auth-back" to="/user-list">
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M19 12H5m0 0 7-7m-7 7 7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {p("Back")}
          </Link>
        )
      }
    >
      {step === STEPS.EMAIL ? (
        <form
          className="mmt-login-card"
          noValidate
          aria-busy={loading}
          onSubmit={submitEmail}
        >
          <h2 tabIndex={-1}>
            {changePassword ? p("Change Password") : t.title}
          </h2>
          <p>{changePassword ? p("password_description") : t.subtitle}</p>
          {error ? (
            <div className="mmt-login-error" role="alert">
              {error}
            </div>
          ) : null}
          <label className="mmt-field">
            <span>{t.email_label}</span>
            <input
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              type="email"
              autoCapitalize="off"
              spellCheck={false}
              inputMode="email"
              autoComplete="email"
              placeholder="name@organization.com"
              disabled={loading || changePassword}
            />
          </label>
          <button
            className="mmt-primary-button"
            type="submit"
            disabled={loading}
          >
            {loading ? t.sending : t.send_code}
          </button>
          <p className="mmt-new-user">
            <Link to={changePassword ? "/user-list" : "/login"}>
              {changePassword ? p("Cancel") : t.back_to_login}
            </Link>
          </p>
        </form>
      ) : null}

      {step === STEPS.RESET ? (
        <form
          className="mmt-login-card"
          noValidate
          aria-busy={loading}
          onSubmit={submitReset}
        >
          <h2 tabIndex={-1}>
            {changePassword ? p("Change Password") : t.reset_title}
          </h2>
          <p>{t.code_sent.replace("{email}", email.trim())}</p>
          {error ? (
            <div className="mmt-login-error" role="alert">
              {error}
            </div>
          ) : null}
          <label className="mmt-field">
            <span>{t.code_label}</span>
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="mmt-code-input"
              disabled={loading}
            />
          </label>
          <div className="mmt-field">
            <label htmlFor="reset-password">{t.new_password}</label>
            <div className="mmt-password-wrap">
              <input
                id="reset-password"
                aria-describedby="reset-password-rules"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                className="mmt-icon-button"
                aria-controls="reset-password"
                aria-pressed={showPassword}
                onClick={() => setShowPassword((value) => !value)}
                aria-label={
                  showPassword
                    ? passwordLabels.hide_password
                    : passwordLabels.show_password
                }
              >
                {showPassword ? passwordLabels.hide : passwordLabels.show}
              </button>
            </div>
            <PasswordRule password={password} id="reset-password-rules" />
          </div>
          <div className="mmt-field">
            <label htmlFor="reset-confirm">{t.confirm_password}</label>
            <div className="mmt-password-wrap">
              <input
                id="reset-confirm"
                type={showConfirm ? "text" : "password"}
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setError("");
                }}
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                className="mmt-icon-button"
                aria-controls="reset-confirm"
                aria-pressed={showConfirm}
                onClick={() => setShowConfirm((value) => !value)}
                aria-label={
                  showConfirm
                    ? passwordLabels.hide_password
                    : passwordLabels.show_password
                }
              >
                {showConfirm ? passwordLabels.hide : passwordLabels.show}
              </button>
            </div>
          </div>
          <button
            className="mmt-primary-button"
            type="submit"
            disabled={loading}
          >
            {loading ? t.saving : t.submit}
          </button>
          <button
            className="mmt-auth-link"
            type="button"
            disabled={loading}
            onClick={() => run(() => requestPasswordReset(email.trim()))}
          >
            {t.send_code}
          </button>
          {changePassword && (
            <p>
              <Link to="/user-list">{p("Cancel")}</Link>
            </p>
          )}
        </form>
      ) : null}

      {step === STEPS.SUCCESS ? (
        <div className="mmt-login-card">
          <h2 tabIndex={-1}>{t.success_title}</h2>
          <p>{t.success_subtitle}</p>
        </div>
      ) : null}
    </AuthLayout>
  );
}
